import React, { useMemo } from "react";
import { Card, Grid, Text } from "@jsq/ds/core";
import type { AgGridReact } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useGetAllGpPositionDetailsQuery } from "../../../../graphql/supergraphHooks";
import { formatCountText } from "../../../../utils/text_utils";
import { provideLegalName } from "../../../ProfileTree/utils";
import { usePositionStatusCounts } from "../../ComplianceStatusCard/usePositionStatusCounts";
import { defaultGridOptions } from "../gridOptions";
import {
  cardHeaderRow,
  clickableGridRow,
  gridWrapper,
  paginationLeft,
} from "../styles_vanilla.css";
import { getPositionColumnDefs } from "./positionColumnDefs";
import { excludedBanner, excludedBannerText } from "./styles_vanilla.css";
import { POSITION_PAGE_SIZE, usePositionDatasource } from "./usePositionDatasource";
import type { PositionRowData } from "../types";

type Props = {
  arenaId: number;
  entityId: number;
  entityName?: string | null;
  navigationCallback: (positionId: number) => void;
};

export const PositionTable: React.FC<Props> = ({
  arenaId,
  entityId,
  entityName,
  navigationCallback,
}) => {
  const datasource = usePositionDatasource(arenaId, entityId);

  const { data } = useGetAllGpPositionDetailsQuery({
    variables: { arenaId, entityId },
  });

  // Positions still pending compliance onboarding ("Review not started") are excluded
  // from the table by the backend, so surface how many were left out.
  const { counts } = usePositionStatusCounts({ arenaId, entityId });
  const excludedCount = counts.reviewNotStarted;
  const isSingle = excludedCount === 1;

  const positionNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of data?.compliancePositions ?? []) {
      map.set(String(p.positionId), provideLegalName(p.investingProfile));
    }
    return map;
  }, [data]);

  const columnDefs = useMemo(
    () => getPositionColumnDefs({ positionNameMap }),
    [positionNameMap],
  );

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = useMemo(
    () => ({
      ...defaultGridOptions,
      rowModelType: "serverSide",
      serverSideDatasource: datasource,
      // Align the grid block size and page size with the backend compliancePositions
      // page size. Without this, cacheBlockSize defaults to 100, the datasource only
      // returns one backend page per block, and the grid treats the short block as the
      // end of data (capping the table). All three must use POSITION_PAGE_SIZE.
      cacheBlockSize: POSITION_PAGE_SIZE,
      paginationPageSize: POSITION_PAGE_SIZE,
      paginationPageSizeSelector: false,
      getRowClass: () => clickableGridRow,
      onRowClicked: (event: { data: PositionRowData }) => {
        const row = event.data;
        if (row?.positionId) {
          navigationCallback(row.positionId);
        }
      },
    }),
    [datasource, navigationCallback],
  );

  return (
    <Card>
      <div className={cardHeaderRow}>
        <Text variant="h3">{`Positions for ${entityName ?? "--"}`}</Text>
      </div>
      {excludedCount > 0 && (
        <div className={excludedBanner} role="status">
          <Icon type="info-circle" color={Colors.Informative700} style={{ paddingTop: 2 }} />
          <Text variant="regular-md" className={excludedBannerText}>
            {`${formatCountText(excludedCount, "position")} with "Review not started" status ${
              isSingle ? "is" : "are"
            } excluded, as ${isSingle ? "it is" : "they are"} pending compliance onboarding.`}
          </Text>
        </div>
      )}
      <div className={`${gridWrapper} ${paginationLeft}`}>
        <Grid<PositionRowData> columnDefs={columnDefs} gridOptions={gridOptions} />
      </div>
    </Card>
  );
};
